import resolvePathname from '../resolve-pathname';
import valueEqual from '../value-equal';
import { LocationState, LocationKey, Location, LocationDescriptor } from '.';

import { parsePath } from './PathUtils';

export function createLocation<S = LocationState>(
  path: LocationDescriptor<S>,
  state?: S,
  key?: LocationKey,
  currentLocation?: Location<S>
): Location<S> {
  let location: Location<S>;

  if (typeof path === 'string') {
    location = {
      ...parsePath(path),
      // @ts-ignore state is S | undefined but Location requires S
      state,
    };
  } else {
    location = {
      pathname: path.pathname ?? '',
      search: path.search ?? '',
      hash: path.hash ?? '',
      state:
        state !== undefined && path.state === undefined ? state : path.state,
      key: path.key,
    } as Location<S>;

    if (location.search && location.search.charAt(0) !== '?')
      location.search = `?${location.search}`;

    if (location.hash && location.hash.charAt(0) !== '#')
      location.hash = `#${location.hash}`;
  }

  try {
    location.pathname = decodeURI(location.pathname);
  } catch (e) {
    if (e instanceof URIError) {
      throw new URIError(
        `Pathname "${location.pathname}" could not be decoded. This is likely caused by an invalid percent-encoding.`
      );
    } else {
      throw e;
    }
  }

  if (key) location.key = key;

  if (currentLocation) {
    // Resolve incomplete/relative pathname relative to current location.
    if (!location.pathname) {
      location.pathname = currentLocation.pathname;
    } else if (location.pathname.charAt(0) !== '/') {
      location.pathname = resolvePathname(
        location.pathname,
        currentLocation.pathname
      );
    }
  } else {
    // When there is no prior location and pathname is empty, set it to /
    if (!location.pathname) {
      location.pathname = '/';
    }
  }

  return location;
}

export function locationsAreEqual<S = LocationState>(
  a: Location<S>,
  b: Location<S>
): boolean {
  return (
    a.pathname === b.pathname &&
    a.search === b.search &&
    a.hash === b.hash &&
    a.key === b.key &&
    valueEqual(a.state, b.state)
  );
}
